import type {FC, MouseEvent, ReactNode} from 'react';
import {useCallback} from 'react';
import type {NativeSyntheticEvent, NativeTouchEvent} from 'react-native';

import type {Props} from './types';

import {createEventNormalizer} from '../../../utils/events';
import {useTimeBlockedCallback} from '../../../utils/hooks/useTimeBlockedCallback';
import {ButtonIcon} from './Icon';
import {ButtonSpinner, ButtonSpinnerContainer} from './Spinner';
import * as S from './styles';
import {ButtonText} from './Text';

type ButtonControllerProps = Props & {
  // for native
  isPressed?: boolean;
};

export const useController = ({
  accentColor,
  children,
  disabled,
  iconLeft,
  iconRight,
  isLoading,
  isPressed,
  onClick,
  scheme,
  size,
  variant,
}: ButtonControllerProps) => {
  const clickHandler = useTimeBlockedCallback(
    useCallback(
      event => {
        const normalized = clickEventNormalizer(event);

        onClick?.(normalized);
      },
      [onClick],
    ),
  );

  const isDisabled = isLoading || disabled;

  const isSingleSpinnerContent = Boolean(isLoading && !iconLeft && !iconRight);

  const isSingleIcon = Boolean(!children && (iconLeft || iconRight));

  const renderSpinner = () => (
    <ButtonSpinner
      accentColor={accentColor}
      buttonSize={size}
      disabled={disabled}
      isSingle={isSingleSpinnerContent}
      scheme={scheme}
      variant={variant}
    />
  );

  const renderIcon = (icon: FC) => (
    <ButtonIcon
      accentColor={accentColor}
      disabled={isDisabled}
      icon={icon}
      isPressed={isPressed}
      scheme={scheme}
      size={size}
      variant={variant}
    />
  );

  const renderLeftIcon = () => {
    if (iconLeft) {
      if (isLoading) {
        return renderSpinner();
      }

      return renderIcon(iconLeft);
    }

    return null;
  };

  const renderRightIcon = () => {
    if (iconRight) {
      if (!children && iconLeft) {
        // eslint-disable-next-line no-console
        console.warn(
          'ButtonV2: IconRight is not rendered because IconLeft is present without text content.',
        );

        return null;
      }

      if (isLoading && !iconLeft) {
        return renderSpinner();
      }

      return renderIcon(iconRight);
    }

    return null;
  };

  const renderIconsIfNeeded = (content: ReactNode) => {
    return (
      <>
        <S.ContentContainer isHidden={isSingleSpinnerContent}>
          {renderLeftIcon()}
          {content}
          {renderRightIcon()}
        </S.ContentContainer>
        {isSingleSpinnerContent ? (
          <ButtonSpinnerContainer>{renderSpinner()}</ButtonSpinnerContainer>
        ) : null}
      </>
    );
  };

  const renderContent = () =>
    typeof children === 'string' && children.length > 0
      ? renderIconsIfNeeded(
          <ButtonText
            accentColor={accentColor}
            buttonVariant={variant}
            disabled={isDisabled}
            isPressed={isPressed}
            scheme={scheme}
            size={size}
          >
            {children}
          </ButtonText>,
        )
      : renderIconsIfNeeded(children);

  return {
    clickHandler,
    isDisabled,
    isSingleIcon,
    renderContent,
  };
};

const clickEventNormalizer = createEventNormalizer<
  MouseEvent,
  NativeSyntheticEvent<NativeTouchEvent>,
  null
>();
